import { cosemApi } from '@services/providers/cosem';
import { Apartment, ApartmentEdition, ApartmentTable, ApartmentToCreate } from './apartment.types';
import { ApiPaginationResponse, ApiResponse } from '@services/providers/cosem.types';
import { Booking, BookingRange, BookingTable, BookingToCreate } from '../booking/booking.types';
import { ActiveFilter } from '@utils/filters';
import { CdnFile } from '@services/core/cdn/file/file';

const apartmentApi = cosemApi.injectEndpoints({
    endpoints: (build) => ({
        getApartments: build.mutation<ApiPaginationResponse<ApartmentTable>, { limit?: number; page?: number; search?: string; orderBy?: string, filters?: ActiveFilter[] }>({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return { 
                    url: `/member/apartments`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "", filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<ApartmentTable>) => response
        }),
        getArchivedApartments: build.mutation<ApiPaginationResponse<ApartmentTable>, { limit?: number; page?: number; search?: string; orderBy?: string, filters?: ActiveFilter[] }>({
            query: ({ limit = 10, page = 1, search, orderBy, filters }) => {
                return { 
                    url: `/member/apartments/archived`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "", filters: JSON.stringify(filters ?? []) }
                };
            },
            transformResponse: (response: ApiPaginationResponse<ApartmentTable>) => response
        }),
        getApartment: build.mutation<ApiResponse<Apartment>, { id: string }>({
            query: ({ id }) => {
                return { 
                    url: `/member/apartment/${id}`, 
                    method: 'GET',
                };
            },
            transformResponse: (response: ApiResponse<Apartment>) => response
        }),
        toggleArchiveApartment: build.mutation<ApiResponse<Apartment>, { id: string|number, archived: boolean }>({
            query: ({ id, archived }) => {
                return { 
                    url: `/member/apartment/${id}/archive`, 
                    method: 'PUT',
                    body: { archived }
                };
            },
            transformResponse: (response: ApiResponse<Apartment>) => response
        }),
        postApartments: build.mutation<ApiResponse<Apartment>, ApartmentToCreate>({
            query: (apartment: ApartmentToCreate) => {
                return { 
                    url: `/member/apartment`, 
                    method: 'POST',
                    body: apartment
                };
            },
            transformResponse: (response: ApiResponse<Apartment>) => response
        }),
        putApartments: build.mutation<ApiResponse<Apartment>, { id: string; apartment: ApartmentToCreate }>({
            query: ({ id, apartment }) => {
                return { 
                    url: `/member/apartment/${id}`, 
                    method: 'PUT',
                    body: apartment    
                };
            },
            transformResponse: (response: ApiResponse<Apartment>) => response
        }),
        getActiveBookings: build.mutation<ApiPaginationResponse<BookingTable>, { id: string, limit?: number, page?: number, search?: string, orderBy?: string }>({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return { 
                    url: `/member/apartment/${id}/bookings/active`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "" }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getAllBookings: build.mutation<ApiPaginationResponse<BookingTable>, { id: string, limit?: number, page?: number, search?: string, orderBy?: string }>({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return { 
                    url: `/member/apartment/${id}/bookings/all`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "" }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getPastBookings: build.mutation<ApiPaginationResponse<BookingTable>, { id: string, limit?: number, page?: number, search?: string, orderBy?: string }>({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return { 
                    url: `/member/apartment/${id}/bookings/past`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "" }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getFutureBookings: build.mutation<ApiPaginationResponse<BookingTable>, { id: string, limit?: number, page?: number, search?: string, orderBy?: string }>({
            query: ({ id, limit = 10, page = 1, search, orderBy }) => {
                return { 
                    url: `/member/apartment/${id}/bookings/future`, 
                    method: 'GET',
                    params: { limit, page, search: search ?? "", orderBy: orderBy ?? "" }
                };
            },
            transformResponse: (response: ApiPaginationResponse<BookingTable>) => response
        }),
        getBookingRange: build.mutation<ApiResponse<BookingRange[]>, { id: string, start: string, end: string }>({
            query: ({ id, start, end }) => {
                return { 
                    url: `/member/apartment/${id}/bookings/range`, 
                    method: 'GET',
                    params: {
                        start: start,
                        end: end
                    }
                };
            },
            transformResponse: (response: ApiResponse<BookingRange[]>) => response
        }),
        postBooking: build.mutation<ApiResponse<Booking>, { id: string, booking: BookingToCreate }>({
            query: ({ id, booking }) => {
                return { 
                    url: `/member/apartment/${id}/booking`, 
                    method: 'POST',
                    body: booking
                };
            },
            transformResponse: (response: ApiResponse<Booking>) => response
        }),
        putBooking: build.mutation<ApiResponse<Booking>, { id: string, bookingId: string, booking: BookingToCreate }>({
            query: ({ id, bookingId, booking }) => {
                return { 
                    url: `/member/apartment/${id}/booking/${bookingId}`, 
                    method: 'PUT',
                    body: booking
                };
            },
            transformResponse: (response: ApiResponse<Booking>) => response
        }),
        deleteBooking: build.mutation<ApiResponse<void>, { id: string, bookingId: string }>({
            query: ({ id, bookingId }) => {
                return { 
                    url: `/member/apartment/${id}/booking/${bookingId}`, 
                    method: 'DELETE',
                };
            },
            transformResponse: (response: ApiResponse<void>) => response
        }),
        getBooking: build.mutation<ApiResponse<Booking>, { id: string, bookingId: string }>({
            query: ({ id, bookingId }) => {
                return { 
                    url: `/member/apartment/${id}/booking/${bookingId}`, 
                    method: 'GET',
                };
            },  
            transformResponse: (response: ApiResponse<Booking>) => response
        }),
        getApartmentEdition: build.mutation<ApiResponse<ApartmentEdition>, { start: string, end: string }>({
            query: ({ start, end }) => {
                return { 
                    url: `/member/apartments/edition`, 
                    method: 'GET',
                    params: {
                        start: start,
                        end: end
                    }
                };
            },
            transformResponse: (response: ApiResponse<ApartmentEdition>) => response
        }),
        /* Documents */
        getApartmentDocuments: build.mutation<ApiResponse<CdnFile[]>, { id: string }>({
            query: ({ id }) => {
                return {
                    url: `/member/apartment/${id}/documents`,
                    method: 'GET'
                };
            },
            transformResponse: (response: ApiResponse<CdnFile[]>) => response
        }),

        deleteApartmentDocument: build.mutation<ApiResponse<void>, { id: string; documentId: string }>({
            query: ({ id, documentId }) => {
                return {
                    url: `/member/apartment/${id}/document/${documentId}`,
                    method: 'DELETE'
                };
            },
            transformResponse: (response: ApiResponse<void>) => response
        }),

        postApartmentDocument: build.mutation<ApiResponse<CdnFile>, { id: string; file: CdnFile }>({
            query: ({ id, file }) => {
                return {
                    url: `/member/apartment/${id}/document`,
                    method: 'POST',
                    body: file
                };
            },
            transformResponse: (response: ApiResponse<CdnFile>) => response
        }),

        checkBookingAvailability: build.mutation<ApiResponse<boolean>, { id: string, start: string, end: string, sharedFirstDay: boolean, sharedLastDay: boolean, bookingId?: string }>({
            query: ({ id, start, end, sharedFirstDay, sharedLastDay, bookingId }) => {
                return {
                    url: `/member/apartment/${id}/bookings/check`,
                    method: 'GET',
                    params: {
                        start,
                        end,
                        sharedFirstDay,
                        sharedLastDay,
                        ...(bookingId ? { bookingId } : {})
                    }
                };
            },
            transformResponse: (response: ApiResponse<boolean>) => response
        }),
    })
});

export const { 
    useGetApartmentsMutation, 
    useGetArchivedApartmentsMutation,
    useGetApartmentMutation,
    usePostApartmentsMutation,
    usePutApartmentsMutation,
    useGetActiveBookingsMutation,
    useGetPastBookingsMutation,
    useGetAllBookingsMutation,
    useGetFutureBookingsMutation,
    useGetBookingRangeMutation,
    usePostBookingMutation,
    usePutBookingMutation,
    useDeleteBookingMutation,
    useGetBookingMutation,
    useToggleArchiveApartmentMutation,
    useGetApartmentEditionMutation,
    useGetApartmentDocumentsMutation,
    useDeleteApartmentDocumentMutation,
    usePostApartmentDocumentMutation,
    useCheckBookingAvailabilityMutation
} = apartmentApi;
